import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
} from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../../providers/AuthProvider";
import { useAppStore } from "../../providers/AppStore";
import useConsultationData, {
  ConsultationData,
} from "./hooks/useConsultationData";
import useAvailableSections from "./hooks/useAvailableSections";
import useStepperNavigation, {
  StepperState,
} from "./hooks/useStepperNavigation";
import { Section, SectionId } from "./components/stepper/steps";
import { setConsultationNavHandler } from "./consultationNavBridge";
import { isDemoConsultationId } from "./data/demo/demoConfig";

type ConsultationContextValue = {
  customerId: string | null;
  role: number;
  data: ConsultationData;
  sections: Section[];
  stepper: StepperState;
  /** True when the user picked "AI Guided" on the splash. Drives the
   *  ElevenLabs consultation agent in `ConsultationScreen`. Sourced from
   *  the URL (`?ai=1`) so the choice survives a tab reload — without that,
   *  the agent would disappear every refresh because the splash isn't
   *  re-shown for mid-flow URLs. */
  aiActive: boolean;
  /** True when the consultation is running as a public, no-auth demo — the URL
   *  `id` is the demo sentinel (`DEMO_CONSULTATION_ID`). In demo mode the page
   *  renders a captured mock snapshot (no network fetches) and the AI agent is
   *  disabled. Consumed by the splash (disables "AI Guided") and the screen
   *  (renders the "DEMO" badge). */
  demoMode: boolean;
  /** Splash's "AI Guided" button calls this. It writes BOTH `ai=1` AND the
   *  stepper-entry params (`section`, `sub`) in a single URL update so the
   *  two state changes can't race (separate `setSearchParams` calls each
   *  read the URL snapshot independently and clobber each other). */
  enterAiGuided: () => void;
};

const ConsultationContext = createContext<ConsultationContextValue | null>(
  null
);

type Props = { children: ReactNode };

export const ConsultationProvider = ({ children }: Props) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { AuthUser } = useAuth();
  // @ts-ignore — AppStoreContext is loosely typed via createContext({})
  const {
    ASSelectedClient,
    ASSetSelectedClient,
    ASGetCustomerPersonalInfo,
  } = useAppStore();

  const urlId = searchParams.get("id");
  const role = AuthUser?.role ?? 0;

  // Public demo mode — the URL `id` is the demo sentinel. Serves a captured
  // snapshot, needs no auth, and disables the AI agent.
  const demoMode = isDemoConsultationId(urlId);

  // For role 1 (customer) we ignore any spoofed `?id=` and use their own
  // PNOĒ user id — NOT `AuthUser.id`, which is the auth identity id and is
  // rejected by the platform APIs (`GetReportv2`, `GetConsultation`, …).
  // Every other platform fetch in the app uses `pnoe_user_id` (see
  // `SubjectProvider.tsx`, `ClientsScreen.tsx:92`, …). Affiliates
  // (role >= 2) trust the URL — the affiliate clients-list passes the
  // selected client's `pnoe_user_id` via the `?id=` param.
  const customerId = useMemo<string | null>(() => {
    if (role === 1) return AuthUser?.pnoe_user_id ?? null;
    return urlId;
  }, [role, urlId, AuthUser?.pnoe_user_id]);

  // Mock fallbacks live inside useConsultationData (it augments the raw
  // dataPlatformServer in-place). Console log + inventory live there too.
  const data = useConsultationData(customerId, role, demoMode);
  const sections = useAvailableSections(data);
  const stepper = useStepperNavigation(sections);

  // Ensure `ASSelectedClient` is populated for the active customer.
  //
  // Why: the ElevenLabs consultation agent reads `secret__token` /
  // `secret__refresh_token` off `ASSelectedClient` (same path the dashboard
  // widget uses). The consultation route is NOT wrapped in `WorkspaceShell`,
  // so the `SubjectProvider` flow that normally calls `ASLoadClientData`
  // (which populates `ASSelectedClient`) never runs —
  // the widget would otherwise mount with empty tokens and the agent's
  // API calls would 401. Two cases:
  //   • role 1 (customer accessing themselves): `AuthUser` IS the customer.
  //     Skip the network call — just synthesize the selected-client object
  //     from `AuthUser` (id + tokens + identity + email + name).
  //   • role ≥ 2 (affiliate viewing a client): fetch the client's tokens
  //     via `ASGetCustomerPersonalInfo` (same path `ASLoadClientData` uses
  //     for the dashboard).
  // Guarded on `ASSelectedClient.id === customerId` so it doesn't re-fire
  // every render once the right client is loaded, and on every URL change
  // that swaps `customerId` (different client → fresh fetch).
  useEffect(() => {
    // Demo mode: no auth, no widget, no client token needed — skip entirely
    // (this effect also makes a network call we must avoid in the demo).
    if (demoMode) return;
    if (!customerId || role <= 0) return;
    if (ASSelectedClient?.id === customerId) return;
    if (role === 1 && AuthUser) {
      // Customer — synthesize from AuthUser. The widget only needs `id`,
      // `token`, and `refresh_token`, but we copy the surrounding shape
      // for parity with what the affiliate fetch produces.
      ASSetSelectedClient({
        role: 1,
        id: AuthUser.pnoe_user_id ?? AuthUser.id,
        token: AuthUser.token,
        refresh_token: AuthUser.refresh_token,
        identity: AuthUser.identity,
        email: AuthUser.email,
        firstName: AuthUser.firstName ?? AuthUser.firstname,
        lastName: AuthUser.lastName ?? AuthUser.lastname,
      });
      return;
    }
    if (role >= 2) {
      // Affiliate — fetch the client's info; this also calls saveCostumer
      // internally, so `ASSelectedClient` gets populated as a side effect.
      // If the fetch fails (e.g. the affiliate is trying to view another
      // affiliate's client and the API returns 401), the helper catches
      // the error, shows a snackbar, and resolves with `undefined`. In
      // that case we mirror the dashboard's `ASLoadClientData` behaviour:
      // navigate the user back to the clients list. The user stays
      // logged in — we don't auto-logout.
      (async () => {
        const result = await ASGetCustomerPersonalInfo(customerId);
        if (result === undefined) {
          navigate("/client/client-list", { replace: true });
        }
      })();
    }
  }, [
    demoMode,
    customerId,
    role,
    AuthUser,
    ASSelectedClient?.id,
    ASSetSelectedClient,
    ASGetCustomerPersonalInfo,
    navigate,
  ]);

  // AI mode is sourced from the URL so it survives reload. We treat any
  // truthy value as "on" (`?ai=1` is the canonical write; `?ai=true` etc.
  // are tolerated for hand-edited URLs). Always OFF in demo mode — the demo is
  // self-guided and has no client token to drive the agent.
  const aiActive = useMemo(() => {
    if (demoMode) return false;
    const v = searchParams.get("ai");
    return v === "1" || v === "true";
  }, [searchParams, demoMode]);

  // Combined entry point used by the splash's "AI Guided" button. Writes
  // `ai=1` plus the stepper-entry params (`section`, `sub`) in ONE
  // `setSearchParams` call. Doing them separately would race — each call
  // builds its `next` from the current `searchParams`, so a sequence of
  // `activateAi(); enterStepper();` would have the second call clobber
  // the first because it never sees the in-flight change.
  const enterAiGuided = useCallback(() => {
    const first = sections[0];
    if (!first || first.subs.length === 0) return;
    const next = new URLSearchParams(searchParams);
    next.set("ai", "1");
    next.set("section", first.id);
    next.set("sub", first.subs[0].id);
    setSearchParams(next, { replace: false });
  }, [searchParams, setSearchParams, sections]);

  // Bridge the ElevenLabs widget's `focusDom` client tool (its virtual
  // `consultation-nav:<section>/<sub>` targets) to the stepper. The tool
  // lives in `useElevenLabsWidget` (reports module) outside React, so it
  // can't call `stepper.goTo` directly — it goes through the module-level
  // bridge instead. `goTo` writes the `section`/`sub` search
  // params (client-side, no reload), so the live agent session survives the
  // jump. We validate against `sections` (the AVAILABLE, per-client filtered
  // list) so a filtered-out step (e.g. body-composition with no InBody) is
  // rejected with feedback rather than silently kicking the user to step 0.
  useEffect(() => {
    setConsultationNavHandler((sectionId, subId) => {
      const section = sections.find((s) => s.id === sectionId);
      if (!section) return { ok: false, error: "unknown_section" };
      // `sub` omitted → land on the first available sub-step of the section.
      if (subId == null || subId === "") {
        stepper.goToSection(section.id);
        return { ok: true };
      }
      const sub = section.subs.find((s) => s.id === subId);
      if (!sub) return { ok: false, error: "unknown_sub" };
      stepper.goTo(section.id as SectionId, sub.id);
      return { ok: true };
    });
    return () => setConsultationNavHandler(null);
  }, [sections, stepper]);

  const value = useMemo<ConsultationContextValue>(
    () => ({
      customerId,
      role,
      data,
      sections,
      stepper,
      aiActive,
      demoMode,
      enterAiGuided,
    }),
    [customerId, role, data, sections, stepper, aiActive, demoMode, enterAiGuided]
  );

  return (
    <ConsultationContext.Provider value={value}>
      {children}
    </ConsultationContext.Provider>
  );
};

export const useConsultation = (): ConsultationContextValue => {
  const ctx = useContext(ConsultationContext);
  if (!ctx) {
    throw new Error(
      "useConsultation must be used inside <ConsultationProvider>"
    );
  }
  return ctx;
};
